import { useNavigate } from "react-router-dom";
import { IconClock, IconFilePencil } from "@tabler/icons-react";
import { useFormFields } from "../../../../hooks/useFormFields";
import { useMe, useMePartner } from "../../../../swr";
import { Input } from "../../../../components/common/input/input";
import { Button } from "../../../../components/common/button/button";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { Banner } from "../../../../components/common/banner";
import { AppCard } from "../../../../components/common/card";
import { Text } from "../../../../components/common/typography";
import { Breadcrumbs } from "../../../../components/common/breadcrumbs";
import { PageContainer } from "../../../../components/common/page-container";
import { AppRoutes } from "../../../../common/routes/app_routes";
import {
  useCreateChangeRequest,
  useUpdateChangeRequest,
} from "../../hooks/useChangeRequestMutations";
import {
  useMyDraftProfileChangeRequest,
  useMyPendingProfileChangeRequest,
} from "../../hooks/useMyPendingProfileChangeRequest";
import {
  CreateChangeRequestDtoTypeEnum,
  type ChangeRequestDto,
  type CreateChangeRequestDto,
} from "../../../../api";
import { humanizeTypeForOwner } from "../../data/change-request-type";
import { getChangeRequestsListPath } from "../../data/change-request-list-path";
import { withDraftValues } from "../../data/profile-draft";
import { formatDate } from "../../../../utils/date";
import { CountryOfOriginSelect as CountrySelect } from "../../../product-requests/presentation/components/country-of-origin-select";
import { StateSelect } from "../../../product-requests/presentation/components/state-select";

const PROFILE_FIELDS = [
  "name",
  "companyName",
  "crNumber",
  "taxNumber",
  "contactName",
  "street",
  "street2",
  "city",
  "zip",
  "countryId",
  "stateId",
] as const;

type ProfileField = (typeof PROFILE_FIELDS)[number];

type ProfileFormFields = Record<ProfileField, string> & { note: string };

const FIELD_LABELS: Record<ProfileField, string> = {
  name: "Full name",
  companyName: "Company name",
  crNumber: "Commercial Registration number",
  taxNumber: "Tax number (VAT)",
  contactName: "Contact name",
  street: "Street address",
  street2: "Street address line 2",
  city: "City",
  zip: "ZIP / postal code",
  countryId: "Country",
  stateId: "State / province",
};

// Phones: PageContainer has no padding below `sm` — keep the 16px side gutters.
export interface ProfileChangeRequestFormProps {
  type: typeof CreateChangeRequestDtoTypeEnum.SellerProfileUpdate |
        typeof CreateChangeRequestDtoTypeEnum.BuyerProfileUpdate;
}

/**
 * One open request per profile is allowed (backend rule), so this page never
 * starts a second one: a saved draft is reopened for editing, and a request
 * already in review is explained instead of failing on submit.
 */
export function ProfileChangeRequestForm({ type }: ProfileChangeRequestFormProps) {
  const { isLoading: isLoadingUser } = useMe();
  const { data: draft, error: draftError } = useMyDraftProfileChangeRequest();
  const { data: pending, error: pendingError } = useMyPendingProfileChangeRequest();
  const isLoadingOpenRequests =
    (draft === undefined && !draftError) || (pending === undefined && !pendingError);

  if (isLoadingUser || isLoadingOpenRequests) {
    return (
      <PageContainer>
        <ShimmerLoader message="Loading your profile and any saved draft…" />
      </PageContainer>
    );
  }

  return (
    <ProfileChangeRequestFormBody
      key={draft?.id ?? "new"}
      type={type}
      draft={draft ?? null}
      pending={pending ?? null}
    />
  );
}

function ProfileChangeRequestFormBody({
  type,
  draft,
  pending,
}: ProfileChangeRequestFormProps & {
  draft: ChangeRequestDto | null;
  pending: ChangeRequestDto | null;
}) {
  const navigate = useNavigate();
  const { user } = useMe();
  const { isBuyer, isSeller } = useMePartner();
  const listPath = getChangeRequestsListPath({ isBuyer, isSeller });

  const liveValues: ProfileFormFields = {
    name: user?.name ?? "",
    companyName: user?.companyName ?? "",
    crNumber: user?.crNumber ?? "",
    taxNumber: user?.taxNumber ?? "",
    contactName: user?.contactName ?? "",
    street: user?.street ?? "",
    street2: user?.street2 ?? "",
    city: user?.city ?? "",
    zip: user?.zip ?? "",
    countryId: user?.country?.id?.toString() ?? "",
    stateId: user?.state?.id?.toString() ?? "",
    note: draft?.note ?? "",
  };

  const { formFields, createChangeHandler, setFormFields } =
    useFormFields<ProfileFormFields>(withDraftValues(liveValues, draft));

  const buildProposedChanges = (): Record<string, unknown> => {
    const diff: Record<string, unknown> = {};
    for (const field of PROFILE_FIELDS) {
      let current = "";
      if (field === "name") current = user?.name ?? "";
      else if (field === "companyName") current = user?.companyName ?? "";
      else if (field === "crNumber") current = user?.crNumber ?? "";
      else if (field === "taxNumber") current = user?.taxNumber ?? "";
      else if (field === "contactName") current = user?.contactName ?? "";
      else if (field === "street") current = user?.street ?? "";
      else if (field === "street2") current = user?.street2 ?? "";
      else if (field === "city") current = user?.city ?? "";
      else if (field === "zip") current = user?.zip ?? "";
      else if (field === "countryId") current = user?.country?.id?.toString() ?? "";
      else if (field === "stateId") current = user?.state?.id?.toString() ?? "";
      const next = formFields[field].trim();
      if (next !== current) {
        if (field === "street") {
          diff.street = next;
        } else if (field === "countryId" || field === "stateId") {
          diff[field] = next ? Number.parseInt(next, 10) : null;
        } else {
          diff[field] = next;
        }
      }
    }
    return diff;
  };

  const buildDto = (): CreateChangeRequestDto => ({
    type,
    proposedChanges: buildProposedChanges(),
    note: formFields.note.trim() || undefined,
  });

  // Hooks can't be called conditionally, so both pairs exist; the draft's id
  // decides which one the buttons use.
  const createDraft = useCreateChangeRequest(buildDto);
  const createAndSubmit = useCreateChangeRequest(buildDto, { submitAfter: true });
  const updateDraft = useUpdateChangeRequest(draft?.id ?? "", buildDto);
  const updateAndSubmit = useUpdateChangeRequest(draft?.id ?? "", buildDto, {
    submitAfter: true,
  });
  const save = draft ? updateDraft : createDraft;
  const submit = draft ? updateAndSubmit : createAndSubmit;

  const diffCount = Object.keys(buildProposedChanges()).length;
  const isBusy = save.isLoading || submit.isLoading;
  const isBlocked = pending != null;

  return (
    <PageContainer>
      <div className="flex flex-col gap-6 w-full min-w-0">
        <Breadcrumbs
          items={[
            { label: "Home", path: AppRoutes.INDEX },
            { label: "My Change Requests", path: listPath },
            { label: humanizeTypeForOwner(type) },
          ]}
        />

        <AppCard title={humanizeTypeForOwner(type)} color="bg-bgDefault" className="w-full">
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (isBlocked || diffCount === 0) return;
              void submit.submitHandler();
            }}
          >
            {isBlocked && (
              <Banner tone="warning" icon={<IconClock size={18} />}>
                Your previous profile change request is still in review. You can
                start a new one once it's approved or rejected, or cancel it from
                Change requests.
              </Banner>
            )}
            {!isBlocked && draft && (
              <Banner tone="warning" icon={<IconFilePencil size={18} />}>
                You're continuing your draft saved {formatDate(draft.updatedAt, true)}.
                Only one profile draft can exist at a time — saving updates it.
              </Banner>
            )}

            <Text variant="bodySm" className="text-textMuted">
              Edit the fields you'd like changed. Only modified fields will be sent for
              review. Email and phone changes aren't handled here — they require
              re-verification.
            </Text>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={FIELD_LABELS.name}
                value={formFields.name}
                onChange={createChangeHandler("name")}
              />
              <Input
                label={FIELD_LABELS.companyName}
                value={formFields.companyName}
                onChange={createChangeHandler("companyName")}
              />
              <Input
                label={FIELD_LABELS.crNumber}
                value={formFields.crNumber}
                onChange={createChangeHandler("crNumber")}
              />
              <Input
                label={FIELD_LABELS.taxNumber}
                value={formFields.taxNumber}
                onChange={createChangeHandler("taxNumber")}
              />
              <Input
                label={FIELD_LABELS.contactName}
                value={formFields.contactName}
                onChange={createChangeHandler("contactName")}
              />
              <Input
                label={FIELD_LABELS.street}
                value={formFields.street}
                onChange={createChangeHandler("street")}
              />
              <Input
                label={FIELD_LABELS.street2}
                value={formFields.street2}
                onChange={createChangeHandler("street2")}
              />
              <Input
                label={FIELD_LABELS.city}
                value={formFields.city}
                onChange={createChangeHandler("city")}
              />
              <Input
                label={FIELD_LABELS.zip}
                value={formFields.zip}
                onChange={createChangeHandler("zip")}
              />
              <CountrySelect
                value={formFields.countryId}
                onChange={(value) =>
                  setFormFields({ ...formFields, countryId: value, stateId: "" })
                }
              />
              <StateSelect
                value={formFields.stateId}
                countryId={formFields.countryId}
                onChange={(value) => setFormFields({ ...formFields, stateId: value })}
              />
            </div>

            <Input
              label="Note to admin (optional)"
              value={formFields.note}
              onChange={createChangeHandler("note")}
              helperText="Short context for the reviewer."
            />

            {!isBlocked && diffCount === 0 && (
              <Text variant="bodyXs" className="text-textMuted">
                No changes yet — edit a field to enable the submit actions.
              </Text>
            )}

            <div className="flex flex-wrap gap-3 pt-2">
              {isBlocked ? (
                <Button
                  type="button"
                  variant="primary"
                  onClick={() =>
                    navigate(AppRoutes.CHANGE_REQUEST_DETAILS.replace(":id", pending.id))
                  }
                >
                  View request in review
                </Button>
              ) : (
                <>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isBusy || diffCount === 0}
                  >
                    {submit.isLoading ? "Submitting..." : "Submit for review"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={isBusy || diffCount === 0}
                    onClick={() => void save.submitHandler()}
                  >
                    {save.isLoading ? "Saving..." : draft ? "Update draft" : "Save draft"}
                  </Button>
                </>
              )}
              <Button
                type="button"
                variant="ghost"
                disabled={isBusy}
                onClick={() => navigate(listPath)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </AppCard>
      </div>
    </PageContainer>
  );
}
